import { z } from "zod";
import { AddressSchema } from "./companies";
import { AssetSchema } from "./assets";
import { ItemSchema } from "./items";

// Sales Order Status enum schema
export const SalesOrderStatusSchema = z.enum([
  "DRAFT",
  "ARCHIVED",
  "FINALIZED",
]);
export type SalesOrderStatus = z.infer<typeof SalesOrderStatusSchema>;
export const SalesOrderStatusEnum = SalesOrderStatusSchema.enum;

// Sales Order Line schema
export const SalesOrderLineSchema = z.object({
  id: z.string(),
  itemId: z.string().nullable(),
  itemNumber: z.string().nullable(),
  revision: z.string().nullable(),
  customerItemNumber: z.string().nullable(),
  customerRevision: z.string().nullable(),
  description: z.string().nullable(),
  quantity: z.string().nullable(),
  unitPrice: z.string().nullable(),

  item: ItemSchema.nullable(),
});

export type SalesOrderLine = z.infer<typeof SalesOrderLineSchema>;

export const SalesOrderLineInputSchema = z.object({
  itemId: z.string().nullable(),
  itemNumber: z.string().nullable(),
  revision: z.string().nullable(),
  customerItemNumber: z.string().nullable(),
  customerRevision: z.string().nullable(),
  description: z.string().nullable(),
  quantity: z.string().nullable(),
  unitPrice: z.string().nullable(),
});

export type SalesOrderLineInput = z.infer<typeof SalesOrderLineInputSchema>;

// Create Sales Order request schema
export const CreateSalesOrderParamsSchema = z.object({
  customerPurchaseOrderNumber: z.string().optional(),
  customerCompanyId: z.string().optional(),
  billingAddress: AddressSchema.optional(),
  deliveryAddress: AddressSchema.optional(),
  quoteId: z.string().optional(),
  primaryFileAssetId: z.string().nullable().optional(),
  fileAssetIds: z.array(z.string()).optional(),
  lines: z.array(SalesOrderLineInputSchema).optional(),
});

export type CreateSalesOrderParams = z.infer<
  typeof CreateSalesOrderParamsSchema
>;

// Update Sales Order request schema
export const UpdateSalesOrderParamsSchema = z.object({
  customerPurchaseOrderNumber: z.string().optional(),
  customerCompanyId: z.string().optional(),
  billingAddress: AddressSchema.optional(),
  deliveryAddress: AddressSchema.optional(),
  quoteId: z.string().optional(),
  primaryFileAssetId: z.string().nullable().optional(),
  fileAssetIds: z.array(z.string()).optional(),
  lines: z.array(SalesOrderLineInputSchema).optional(),
  status: SalesOrderStatusSchema.optional(),
});

// Sales Order response schema
export const SalesOrderSchema = z.object({
  id: z.string(),
  status: SalesOrderStatusSchema,
  salesOrderNumber: z.number(),
  revision: z.number(),
  customerPurchaseOrderNumber: z.string().nullable(),
  customerCompanyId: z.string().nullable(),
  billingAddress: AddressSchema.nullable(),
  deliveryAddress: AddressSchema.nullable(),
  quoteId: z.string().nullable(),
  primaryFileAssetId: z.string().nullable(),
  fileAssetIds: z.array(z.string()),
  lines: z.array(SalesOrderLineSchema),
  organizationId: z.string(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  // Optional hydrated fields
  primaryFileAsset: AssetSchema.nullable(),
});

export type SalesOrder = z.infer<typeof SalesOrderSchema>;

export type UpdateSalesOrderParams = z.infer<
  typeof UpdateSalesOrderParamsSchema
>;

export const UpdateSalesOrderResponseSchema = z.object({
  salesOrder: SalesOrderSchema,
});

export type UpdateSalesOrderResponse = z.infer<
  typeof UpdateSalesOrderResponseSchema
>;

// Get Sales Order by number query params schema
export const GetSalesOrderByNumberQuerySchema = z.object({
  salesOrderNumber: z.string(),
  revision: z.string().optional(),
});

export type GetSalesOrderByNumberQuery = z.infer<
  typeof GetSalesOrderByNumberQuerySchema
>;

// Update Sales Order status request schema
export const UpdateSalesOrderStatusParamsSchema = z.object({
  status: SalesOrderStatusSchema,
});

export type UpdateSalesOrderStatusParams = z.infer<
  typeof UpdateSalesOrderStatusParamsSchema
>;

// Filter for searching sales orders
export const SalesOrderFilterSchema = z.object({
  search: z.string().optional(),
  ids: z.array(z.string()).optional(),
  salesOrderNumbers: z.array(z.number()).optional(),
});

export type SalesOrderFilter = z.infer<typeof SalesOrderFilterSchema>;

// List Sales Orders query schema
export const ListSalesOrdersQuerySchema = z.object({
  search: z.string().optional(),
  ids: z.string().optional(),
  salesOrderNumbers: z.string().optional(),
});

export type ListSalesOrdersQuery = z.infer<typeof ListSalesOrdersQuerySchema>;

// Path params schemas
export const SalesOrderIdParamsSchema = z.object({
  id: z.string(),
});

export type SalesOrderIdParams = z.infer<typeof SalesOrderIdParamsSchema>;

// List Sales Orders response schema
export const ListSalesOrdersResponseSchema = z.object({
  salesOrders: z.array(SalesOrderSchema),
});

export type ListSalesOrdersResponse = z.infer<
  typeof ListSalesOrdersResponseSchema
>;
